import { FC } from "react";
import Select from "react-select";
import { Badge } from "react-bootstrap";
import useEmailTemplates from "../../../hooks/useEmailTemplates";
import { sortTemplates } from "../../../utils/sortTemplates";
import { normalizeForSearch, stripHtmlToText } from "../../../utils/htmlText";
import {
  SYSTEM_TEMPLATES_LABEL,
  CUSTOM_REPLIES_LABEL,
  NO_MATCH_MESSAGE,
  EMPTY_GROUP_MESSAGE,
  RETRY_BUTTON_LABEL,
} from "./TemplatePicker.constants";

export interface ITemplatePickerTemplate {
  id: number;
  name: string;
  message: string;
  addMessage: string;
}

export interface ITemplatePickerProps {
  onSelect: (template: ITemplatePickerTemplate) => void;
  disabled?: boolean;
}

interface IOption {
  value: number;
  label: string;
  priority: number;
  isCustom: boolean;
  template: ITemplatePickerTemplate;
}

// react-select's own `FilterOptionOption<Option>` type isn't re-exported from
// the package root in this version, so it's reproduced (structurally) here.
interface IFilterOptionOption {
  readonly label: string;
  readonly value: string;
  readonly data: IOption;
}

const filterOption = (
  option: IFilterOptionOption,
  rawInput: string
): boolean => {
  const needle = normalizeForSearch(rawInput);
  if (!needle) {
    return true;
  }

  const haystackLabel = normalizeForSearch(option.label);
  const haystackBody = normalizeForSearch(
    stripHtmlToText(option.data.template.message)
  );

  return haystackLabel.includes(needle) || haystackBody.includes(needle);
};

const TemplatePicker: FC<ITemplatePickerProps> = ({ onSelect, disabled }) => {
  const { templates, isLoading, error, reload } = useEmailTemplates();

  if (error) {
    return (
      <div className="d-flex align-items-center gap-2">
        <span className="text-danger">{error}</span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={reload}
        >
          {RETRY_BUTTON_LABEL}
        </button>
      </div>
    );
  }

  const options: IOption[] = sortTemplates(templates).map((template) => ({
    value: template.id,
    label: template.name,
    priority: template.priority,
    isCustom: template.isCustom,
    template: {
      id: template.id,
      name: template.name,
      message: template.message,
      addMessage: template.addMessage,
    },
  }));

  return (
    <div className="d-flex flex-wrap gap-2">
      {[
        { isCustom: false, label: SYSTEM_TEMPLATES_LABEL },
        { isCustom: true, label: CUSTOM_REPLIES_LABEL },
      ].map((group) => (
        <div key={group.label} style={{ minWidth: "220px", flex: "1 1 220px" }}>
          <Select<IOption>
            classNamePrefix="template-picker"
            aria-label={group.label}
            options={options.filter(
              (option) => !!option.isCustom === group.isCustom
            )}
            value={null}
            placeholder={group.label}
            isSearchable
            isClearable={false}
            isDisabled={disabled}
            isLoading={isLoading}
            controlShouldRenderValue={false}
            filterOption={filterOption}
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? NO_MATCH_MESSAGE
                : EMPTY_GROUP_MESSAGE
            }
            formatOptionLabel={(option) => (
              <span className="d-flex align-items-center gap-2">
                <Badge bg="secondary" pill>
                  {option.priority}
                </Badge>{" "}
                <span>{option.label}</span>
              </span>
            )}
            onChange={(option) => {
              if (option) {
                onSelect(option.template);
              }
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default TemplatePicker;
